import { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { DateRangePicker, DateRangeValue } from '../ui/date-picker';
import { RefreshCw } from 'lucide-react';
import { QuoteAnalyticsTab } from './QuoteAnalyticsTab';
import type { QuoteAnalyticsDomain } from '../../services/quote-analytics.service';

interface QuoteAnalyticsSectionProps {
  domain: QuoteAnalyticsDomain;
  accessToken: string;
}

// Helper to get date in YYYY-MM-DD format
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Default: First day of current month to today
function getDefaultDateRange(): DateRangeValue {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  oneMonthAgo.setHours(0, 0, 0, 0);

  return { from: oneMonthAgo, to: today };
}

export function QuoteAnalyticsSection({ domain, accessToken }: QuoteAnalyticsSectionProps) {
  const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(getDefaultDateRange());
  const [refreshKey, setRefreshKey] = useState(0);

  // Convert DateRangeValue (Date objects) to string format for the API
  const apiDateRange = useMemo(() => {
    if (!dateRange?.from) {
      const today = new Date();
      const firstOfMonth = new Date();
      firstOfMonth.setDate(1);
      return { startDate: formatDate(firstOfMonth), endDate: formatDate(today) };
    }
    return {
      startDate: formatDate(dateRange.from),
      endDate: dateRange.to ? formatDate(dateRange.to) : formatDate(dateRange.from)
    };
  }, [dateRange]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <div className="space-y-6">
      {/* Date Range Picker + Refresh */}
      <div className="flex flex-wrap items-center gap-4">
        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          placeholder="Selecionar período"
        />

        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Analytics Tab Content */}
      <QuoteAnalyticsTab
        domain={domain}
        accessToken={accessToken}
        dateRange={apiDateRange}
        key={`${domain}-${refreshKey}`}
      />
    </div>
  );
}
